import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { ItemPerformanceService } from "@shared/services/item-performance.service";
import { ChartModule } from "primeng/chart";
import 'chartjs-adapter-date-fns';
import { Util } from "pdfjs-dist";
import { Utils } from "@shared/utils";

@Component({
    standalone: true,
    selector: 'app-daily-summary-widget',
    imports: [CommonModule, ChartModule],
    templateUrl: "./dailysummarywidget.html"
})
export class DailySummaryWidget {
    todayStart!: Date;
    todayEnd!: Date;

    revenue_chartData: any = {};
    profit_chartData: any = {};
    isDailySummaryLoading: boolean = true;

    profit_chartOptions: any = {
        plugins: {
            title: {
                display: false,
                // text: 'Number of Items Sold'
            },
            legend: {
                display: false
            },
        },
        responsive: true,

        scales: {
            xAxes: {
                type: 'time',
                stacked: true,
                // bounds:'ticks',
                time: {
                    tooltipFormat: 'YYYY-MM-DD HH:mm',
                    unit: 'day',

                    displayFormats: {
                        day: 'MMM dd'
                    }

                },
                ticks: {
                    callback: (value: any) => {
                        const date = new Date(value);
                        return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit' }).format(date);
                    }
                }
            },
            yAxes: {
                display: true,
                stacked: true,
                ticks: {
                    // Include a dollar sign in the ticks
                    callback: (value: any, index: number, ticks: number) => {

                        return '$' + this.abbreviateNumber(value);
                    }
                }

            }
        }
    };
    revenue_chartOptions: any = {};

    constructor(private itemPerformanceService: ItemPerformanceService) {
    }
    initChartOptions() {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--p-text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
        const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');
        this.revenue_chartOptions = {
            maintainAspectRatio: false,
            aspectRatio: 1.2,
            plugins: {
                tooltip: {
                    mode: 'index',
                    intersect: false
                },
                legend: {
                    display: false,
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    stacked: true,
                    
                    type: 'time', // Use time scale for the x-axis
                    time: {
                        unit: 'day', // Set the unit to 'day'
                        displayFormats: {
                            day: 'MMM dd',
                        },
                        grid: {
                            //color: surfaceBorder,
                            drawBorder: false
                        }
                    },
                    ticks: {
                        color: textColorSecondary,
                        autoSkip: true,
                        maxRotation: 0,
                        callback: (value: any) => {
                            const date = new Date(value);
                            return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit' }).format(date);
                        }
                      },
                },
                y: {
                    stacked: true,
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };
    }
    ngOnInit() {
        this.initChartOptions();
        this.todayStart = new Date(new Date().toDateString());
        this.todayEnd = new Date(this.todayStart.getTime() + (1000 * 60 * 60 * 24));
        this.loadDailySalesSummaries();
    }
    loadDailySalesSummaries() {
        this.isDailySummaryLoading = true;
        const startDate = new Date(this.todayStart.getTime() - (1000 * 60 * 60 * 24 * 7));
        this.itemPerformanceService.getDailySalesStorefrontwise(startDate, this.todayEnd)
            .subscribe((result) => {
                this.isDailySummaryLoading = false;
                console.log(result);
                this.revenue_chartData = this.getGraphDataset(result, 'income');
                this.profit_chartData = this.getGraphDataset(result, 'profit');
            });
    }
    getGraphDataset(dailyItemSummary: any[], parameter: string) {
        const amazon = { data: [] as { x: any, y: any }[], label: 'Amazon', borderColor: 'gray', backgroundColor: Utils.storeFrontColors.Amazon, fill: false };
        const fba = { data: [] as { x: any, y: any }[], label: 'Fba', borderColor: 'gold', backgroundColor: Utils.storeFrontColors.Fba, fill: false };
        const ebay = { data: [] as { x: any, y: any }[], label: 'Ebay', borderColor: 'red', backgroundColor: Utils.storeFrontColors.Ebay, fill: false };
        const shopify = { data: [] as { x: any, y: any }[], label: 'Shopify', borderColor: 'green', backgroundColor: Utils.storeFrontColors.Shopify, fill: false };
        const walmart = { data: [] as { x: any, y: any }[], label: 'Walmart', borderColor: 'blue', backgroundColor: Utils.storeFrontColors.Walmart, fill: false };
        const wfs = { data: [] as { x: any, y: any }[], label: 'Wfs', borderColor: Utils.storeFrontColors.Wfs, backgroundColor: Utils.storeFrontColors.Wfs, fill: false };
        const tikTok = { data: [] as { x: any, y: any }[], label: 'TikTok', borderColor: Utils.storeFrontColors.TikTok, backgroundColor: Utils.storeFrontColors.TikTok, fill: false };
        const fbt = { data: [] as { x: any, y: any }[], label: 'Fbt', borderColor: Utils.storeFrontColors.Fbt, backgroundColor: Utils.storeFrontColors.Fbt, fill: false };

        // const stockCount={ data:[], type: 'line', label:'Stock',borderColor: 'black',yAxisID: 'itemcount'}

        dailyItemSummary.forEach((dailySummary) => {
            const data = dailySummary.storefrontStats;
            if (data.Amazon && data.Amazon[parameter]) {
                amazon.data.push({ x: dailySummary.fromDate, y: data.Amazon[parameter] });
            }
            if (data.Fba && data.Fba[parameter]) {
                fba.data.push({ x: dailySummary.fromDate, y: data.Fba[parameter] });
            }
            if (data.Wfs && data.Wfs[parameter]) {
                wfs.data.push({ x: dailySummary.fromDate, y: data.Wfs[parameter] });
            }
            if (data.TikTok && data.TikTok[parameter]) {
                tikTok.data.push({ x: dailySummary.fromDate, y: data.TikTok[parameter] });
            }
            if (data.Fbt && data.Fbt[parameter]) {
                fbt.data.push({ x: dailySummary.fromDate, y: data.Fbt[parameter] });
            }
            if (data.Ebay && data.Ebay[parameter]) {
                ebay.data.push({ x: dailySummary.fromDate, y: data.Ebay[parameter] });
            }
            if (data.Shopify && data.Shopify[parameter]) {
                shopify.data.push({ x: dailySummary.fromDate, y: data.Shopify[parameter] });
            }
            if (data.Walmart && data.Walmart[parameter]) {
                walmart.data.push({ x: dailySummary.fromDate, y: data.Walmart[parameter] });
            }
            //  stockCount.data.push({ x: dailySummary.fromDate, y: data.Walmart.[parameter] });

        });
        // this.chartData[0] = amazon;
        // this.chartData[1] = fba;
        // this.chartData[2] = ebay;
        // this.chartData[3] = shopify;
        // this.chartData[4] = walmart;
        return { datasets: [amazon, fba, ebay, shopify, walmart, wfs, tikTok, fbt] };
        //this.chartData[4] = stockCount;

    }
    abbreviateNumber(number: number) {
        const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];
        // what tier? (determines SI symbol)
        var tier = Math.log10(Math.abs(number)) / 3 | 0;

        // if zero, we don't need a suffix
        if (tier == 0) return number;

        // get suffix and determine scale
        var suffix = SI_SYMBOL[tier];
        var scale = Math.pow(10, tier * 3);

        // scale the number
        var scaled = number / scale;

        // format number and add suffix
        return scaled.toFixed(1) + suffix;
    }
}